/* eslint-disable import/no-cycle */
import { createNewDate, createNewDuration } from '@mixins/moment-mixin.js';
import { ITEM_TYPE_CHARGE } from '@models/item';
import Model from '@models/model';
import Order from '@models/order';
import Route from '@models/route';
import sumBy from 'lodash/sumBy';

export const ROUTE_TASK_STATUS_COMPLETED = 'Completed';
export const ROUTE_TASK_STATUS_ARRIVED = 'Arrived';
export default class RouteTask extends Model {
    static className = 'RouteTask';

    order = null;

    route = null;

    constructor(data) {
        super();
        this.setAttributes(data);
    }

    resource() {
        return 'tasks';
    }

    get propCasts() {
        return {
            arrived_at: createNewDate,
            completed_at: createNewDate,
            estimated_start_time: createNewDate,
        };
    }

    relations() {
        return {
            order: Order,
            route: Route,
        };
    }

    hasSameCompletionTimeAsPreviousTask(routeTasks) {
        const currentIndex = routeTasks.findIndex((task) => task.id === this.id);
        if (currentIndex > 0) {
            const previousTask = routeTasks[currentIndex - 1];

            if (previousTask.completed_at && previousTask.completed_at.isSame(this.completed_at.format('YYYY-MM-DD HH:mm'))) {
                const timeDifference = createNewDuration(previousTask.completed_at.diff(this.completed_at));
                return timeDifference.asMinutes() === 0;
            }
        }
        return false;
    }

    getEstimatedFinishTime(routeTasks) {
        if (this.status === ROUTE_TASK_STATUS_ARRIVED) {
            const groupedTasks = routeTasks
                .filter((t) => t.status === ROUTE_TASK_STATUS_ARRIVED)
                .filter((rt) => {
                    const routeTaskDate = rt.arrived_at;
                    const currentTaskDate = this.arrived_at;
                    const timeDifference = createNewDuration(routeTaskDate.diff(currentTaskDate));

                    return timeDifference.asMinutes() === 0;
                });

            const totalDuration = sumBy(groupedTasks, 'order.duration');

            return this.arrived_at.clone().add(totalDuration, 'minutes').format('h:mm a');
        }

        const groupedTasks = routeTasks
            .filter((rt) => rt.order_id)
            .filter((rt) => {
                const routeTaskDate = rt.estimated_start_time;
                const currentTaskDate = this.estimated_start_time;
                const timeDifference = createNewDuration(routeTaskDate.diff(currentTaskDate));

                return timeDifference.asMinutes() === 0;
            });
        const totalDuration = sumBy(groupedTasks, (gt) => Number(gt.order.duration));

        return this.estimated_start_time.clone().add(totalDuration, 'minutes').format('h:mm a');
    }

    getTotalLineItemQty() {
        return sumBy(this.order.items.filter((i) => i.item_type_id !== ITEM_TYPE_CHARGE), (item) => item.qty);
    }

    getTotalLineItemDeliveredQty() {
        return sumBy(this.order.items.filter((i) => i.item_type_id !== ITEM_TYPE_CHARGE), (item) => item.delivered_qty);
    }

    getActualDurationInMinutes() {
        return createNewDuration(this.completed_at.diff(this.arrived_at)).asMinutes();
    }

    isOrderLate() {
        if (this.order.customer_location.delivery_window_time) {
            const taskStartTime = this.estimated_start_time;
            const time = createNewDate(this.order.customer_location.delivery_window_time, 'h:mm a');
            const endTime = createNewDate(this.order.customer_location.delivery_window_time_end, 'h:mm a');

            // eslint-disable-next-line default-case
            switch (this.order.customer_location.delivery_window_type) {
            case 'before':
                return taskStartTime.isAfter(time);
            case 'after':
                return taskStartTime.isBefore(time);
            case 'between':
                return !taskStartTime.isSameOrAfter(time) || !taskStartTime.isSameOrBefore(endTime);
            }
        }
        return false;
    }

    totalDelivered() {
        return this.status === ROUTE_TASK_STATUS_COMPLETED ? this.getTotalLineItemDeliveredQty() : '-';
    }

    totalPickedUp() {
        const total = sumBy(this.order.items.filter((i) => i.item_type_id !== ITEM_TYPE_CHARGE), 'pickup_qty');

        return this.status === ROUTE_TASK_STATUS_COMPLETED ? total : '-';
    }

    getTanks() {
        return this.order.items.filter((item) => item.isTank());
    }

    isCompleted() {
        return this.status === ROUTE_TASK_STATUS_COMPLETED;
    }
}
